import { useMemo, useState } from 'react';
import Image from 'next/image';
import { NextLink } from '@common/NextLink/NextLink';
import type { LanguageAlternate } from '@models/data';
import classNames from 'classnames';
import { useDebounce } from 'use-debounce';
import { LocaleSelection } from '@layout/LocaleSelection/LocaleSelection';
import { useTranslation } from 'next-i18next';
import { ContactPersonBox } from '@common/ContactPersonBox/ContactPersonBox';
import { MobileMainMenu, MobileMainMenuButton } from '@layout/MobileMainMenu/MobileMainMenu';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { getLocale } from '@libs/constants';
import styles from './MainMenu.module.scss';
import { useRouter } from 'next/router';
import type { ContactAreaType } from '@models/api/contact-area-type.types';
import type { ProductFragmentEnhanced } from '@models/enhanced-fragments';
import { IGUS_REWRITE } from '@libs/redirect.helper';

const inactiveIndex = -1;

export interface PopupWindowConfig {
  width: number;
  height: number;
  name?: string;
}

export interface MainMenuItem {
  /* eslint-disable react/no-unused-prop-types */
  // Unused prop check is disabled for this block because ESLint does not pick
  // up the use of these properties almost immediately after this type
  // declaration.

  title: string;
  icon?: string;
  url?: string;
  internal?: boolean;
  children?: MainMenuItem[];
  popup?: PopupWindowConfig;
  hasNoSubItems?: boolean;
}

export const hasNoSubItemsClasses = 'hover:text-igus-primary ![text-shadow:_none] hover:font-bold';

export const LinkItem = ({ internal, title, popup, url, hasNoSubItems }: MainMenuItem) => {
  const router = useRouter();
  const locale = getLocale(router);
  if (internal) {
    return (
      <NextLink href={url} aria-label={title} className={hasNoSubItems ? hasNoSubItemsClasses : ''}>
        {title}
      </NextLink>
    );
  }
  if (popup) {
    return (
      <a
        className={hasNoSubItems ? hasNoSubItemsClasses : ''}
        href="#0"
        onClick={() =>
          window &&
          window.open(IGUS_REWRITE(url, locale), popup.name, `innerWidth=${popup.width},innerHeight=${popup.height}`)
        }
      >
        {title}
      </a>
    );
  }

  return (
    <a className={hasNoSubItems ? hasNoSubItemsClasses : ''} href={IGUS_REWRITE(url, locale)} aria-label={title}>
      {title}
    </a>
  );
};

export const LinkListSubItem = (props: MainMenuItem) => (
  <li className="my-4 font-normal">
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <LinkItem {...props} />
  </li>
);

type LinkListItemProps = MainMenuItem & {
  cols: number;
  hasNoSubItems?: boolean;
};
export const LinkListItem = ({ title, children, url, cols, internal, popup, hasNoSubItems }: LinkListItemProps) => (
  <li className={`inline-block align-top ${hasNoSubItems ? 'px-2' : 'px-10'} w-1/${cols}}`}>
    {url ? (
      <LinkItem key={url} internal={internal} title={title} url={url} popup={popup} hasNoSubItems={hasNoSubItems} />
    ) : (
      <div className="mb-3 font-bold border-b border-black border-solid">{title}</div>
    )}
    {children && (
      <ul>
        {children.map((child, index) => (
          <LinkListSubItem
            // eslint-disable-next-line react/no-array-index-key
            key={`${child.url}-${index}`}
            internal={child.internal}
            title={child.title}
            url={child.url}
            popup={child.popup}
          >
            {child.children}
          </LinkListSubItem>
        ))}
      </ul>
    )}
  </li>
);

export interface MainMenuLinkListProps {
  active: boolean;
  items: MainMenuItem[];
}

const getHasNoSubItems = (items: MainMenuItem[]) => !items.some(item => item.children?.length);

export const MainMenuLinkList = ({ items, active }: MainMenuLinkListProps) => {
  const cols = items.length < 4 ? 4 : items.length;
  const hasNoSubItems = useMemo(() => getHasNoSubItems(items), [items]);
  return (
    <ul className={classNames(active ? '' : 'hidden', 'py-10 container flex flex-wrap')}>
      {items.map((item, index) => (
        <LinkListItem
          /* eslint-disable-next-line react/no-array-index-key */
          key={`${item.title}${item.children?.length > 0 ? item.children[0].url : ''}-${index}`}
          title={item.title}
          icon={item.icon}
          url={item.url}
          internal={item.internal}
          popup={item.popup}
          cols={cols}
          hasNoSubItems={hasNoSubItems}
        >
          {item.children}
        </LinkListItem>
      ))}
    </ul>
  );
};

type CategoryTabsItemProps = MainMenuItem & {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  active: boolean;
};

const CategoryTabsItem = ({ icon, title, active, onMouseEnter, onMouseLeave }: CategoryTabsItemProps) => (
  <li
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    className={classNames('inline-block text-center flex-col', active ? 'bg-gray-100 text-igus-primary' : '')}
    style={{ width: '170px' }}
  >
    <div className="pt-6 pb-9 px-7">
      {icon && (
        <div className="block m-auto mb-5">
          <Image
            unoptimized
            data-testid="main-menu-icon"
            width={80}
            height={40}
            src={icon}
            alt={title}
            style={{
              maxWidth: '100%',
              height: 'auto',
              margin: '0 auto',
            }}
          />
        </div>
      )}
      {/* eslint-disable-next-line react/no-danger */}
      <div dangerouslySetInnerHTML={{ __html: title }} />
    </div>
  </li>
);

export interface MainMenuCategoryTabsProps {
  items: MainMenuItem[];
}

export const MainMenuCategoryTabs = ({ items }: MainMenuCategoryTabsProps) => {
  const [activeTab, setActiveTab] = useState(inactiveIndex);

  return (
    <>
      <ul className="container flex">
        {items.map((item: MainMenuItem, index) => (
          <CategoryTabsItem
            /* eslint-disable-next-line react/no-array-index-key */
            key={`${item.title}-tab-${index}`}
            icon={item.icon}
            title={item.title}
            active={activeTab === index}
            onMouseEnter={() => setActiveTab(index)}
            onMouseLeave={() => setActiveTab(inactiveIndex)}
          />
        ))}
      </ul>
      {items.map(
        (item, index) =>
          item.children && (
            <div
              /* eslint-disable-next-line react/no-array-index-key */
              key={`${item.title}-pane-${index}`}
              className="bg-gray-100"
              onMouseEnter={() => setActiveTab(index)}
              onMouseLeave={() => setActiveTab(inactiveIndex)}
            >
              <MainMenuLinkList items={item.children} active={activeTab === index} />
            </div>
          )
      )}
    </>
  );
};

export interface MainMenuPaneProps {
  active: boolean;
  items: MainMenuItem[];
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export const MainMenuPane = ({ active, onMouseEnter, onMouseLeave, items }: MainMenuPaneProps) => (
  <div
    className={classNames('absolute bg-gray-50 w-full z-50 shadow-lg-dark', active ? '' : 'hidden')}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {items[0].icon ? <MainMenuCategoryTabs items={items} /> : <MainMenuLinkList items={items} active />}
  </div>
);

export interface MainMenuProps {
  languageAlternates: ReadonlyArray<LanguageAlternate>;
  contactAreaType: ContactAreaType | null;
  mainMenuItems: MainMenuItem[];
  currentProduct?: ProductFragmentEnhanced;
}

interface MenuItemProps {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  active: boolean;
  title: string;
}

const MenuItem = ({ title, active, onMouseEnter, onMouseLeave }: MenuItemProps) => (
  <li
    className={classNames('inline-block text-white py-7 px-12', active ? 'bg-igus-primary-600' : '')}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {title}
  </li>
);

export const MainMenu = ({ languageAlternates, contactAreaType, mainMenuItems, currentProduct }: MainMenuProps) => {
  const [activeMenu, setActiveMenu] = useState(inactiveIndex);
  const [showContactPerson, setShowContactPerson] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [debouncedActiveMenu] = useDebounce(activeMenu, 250);
  const { lg, available } = useBreakpoints();
  const { t } = useTranslation('layout/contactPersonBox');

  const menuHidden = debouncedActiveMenu === inactiveIndex || activeMenu === inactiveIndex;

  // render menu hidden initially because we don't know which menu to render on the server, but need to include the dom due to seo

  const setShowMobileMenuWithOverflowHidden = (visible: boolean) => {
    if (window.document && window.document.body) {
      window.document.body.classList[visible ? 'add' : 'remove']('overflow-hidden');
    }

    setShowMobileMenu(visible);
  };

  if (lg || !available) {
    return (
      <nav
        className={classNames(
          styles.navBar,
          'bg-igus-primary text-sm font-medium leading-9 cursor-pointer igus-text-styles w-full lg:visible'
        )}
        data-testid="mainmenu-large-display"
      >
        <ul className="container flex px-6">
          {mainMenuItems.map((item, index) => (
            <MenuItem
              /* eslint-disable-next-line react/no-array-index-key */
              key={`${item.title}-${index}`}
              active={activeMenu === index}
              title={item.title}
              onMouseEnter={() => setActiveMenu(index)}
              onMouseLeave={() => setActiveMenu(inactiveIndex)}
            />
          ))}
          <li className="flex-auto" />
          <li
            data-testid="test-item-contact-person"
            className={classNames(
              'inline-block text-white py-7 px-8 whitespace-nowrap',
              showContactPerson ? 'bg-igus-primary-600' : 'bg-igus-primary'
            )}
            onMouseEnter={() => setShowContactPerson(true)}
            onMouseLeave={() => {
              setShowContactPerson(false);
            }}
          >
            {t('CONTACT_PERSON_BOX.HEADLINE')}
          </li>
          <li className="inline-block px-12 py-7">
            <LocaleSelection languageAlternates={languageAlternates} currentProduct={currentProduct} />
          </li>
        </ul>
        <div className="relative">
          {mainMenuItems.map(
            (item, index) =>
              item.children &&
              item.children.length > 0 && (
                <MainMenuPane
                  /* eslint-disable-next-line react/no-array-index-key */
                  key={`${item.title}-${index}`}
                  active={menuHidden ? debouncedActiveMenu === index : activeMenu === index}
                  items={item.children}
                  onMouseEnter={() => setActiveMenu(index)}
                  onMouseLeave={() => setActiveMenu(inactiveIndex)}
                />
              )
          )}
          <div
            className="container relative"
            onMouseEnter={() => {
              setShowContactPerson(true);
            }}
            onMouseLeave={e => {
              if ((e.target as HTMLElement)?.nodeName?.toLowerCase() === 'select') {
                // don't close popup if we clicked a select element
                return;
              }
              setShowContactPerson(false);
            }}
          >
            <ContactPersonBox visible={showContactPerson} contactAreaType={contactAreaType} />
          </div>
        </div>
      </nav>
    );
  }
  return (
    <>
      <div
        className="w-full px-8 pt-1 pb-2 text-right text-white bg-igus-primary min-h-8 lg:invisible"
        style={{ fontSize: '2rem' }}
      >
        <MobileMainMenuButton onClick={() => setShowMobileMenuWithOverflowHidden(true)} />
      </div>
      <MobileMainMenu
        visible={showMobileMenu}
        onClose={() => setShowMobileMenuWithOverflowHidden(false)}
        mainMenuItems={mainMenuItems}
      />
    </>
  );
};
